import styled from '@emotion/styled';
import {Container, media, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {IntroHeader} from 'UI/components/layout/IntoHeader';
import {H3} from 'UI/components/layout/Text';
import {ViewportTrigger} from 'UI/components/layout/ViewportTrigger';
import {Card} from 'UI/components/modules/FeatureGrid/Card';

import {FadeInAnimation} from 'UI/animations/shared';

import type {FeatureGridType} from 'types/payload-types';

/**
 * The props for the `FeatureGrid` component, which renders a grid of feature cards
 * with optional owner-specific features and an introductory header.
 * This interface defines the structure for the general features, owner-specific
 * features, and introductory content. It also supports a `testId` for automated
 * testing.
 */
interface ComponentProps {
    /**
     * An optional list of general features to display.
     * Each feature typically includes an ID, headline, image, and descriptive text.
     */
    features?: FeatureGridType['features'];
    /**
     * An optional section of features specifically for owners.
     * May include a headline and a list of features. If `forOwners` is not true,
     * this section will not be rendered.
     */
    featuresOwners: FeatureGridType['featuresOwners'];
    /**
     * An optional flag to hide the entire block when set to true.
     * When `true`, the component renders nothing.
     *
     * @default false
     */
    hideBlock?: FeatureGridType['hideBlock'];
    /**
     * An optional identifier applied as the `id` attribute of the intro header.
     * Useful for in-page navigation and accessibility (e.g., anchor links).
     */
    id?: FeatureGridType['id'];
    /**
     * The intro section configuration, which may include an overline, headline,
     * and supporting descriptive text. If omitted, no intro is rendered.
     */
    intro?: FeatureGridType['intro'];
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'FeatureGrid'
     */
    testId?: string;
}

/**
 * A feature grid component that displays a set of feature cards with optional owner-specific features.
 * The `FeatureGrid` component renders an introductory section using `IntroHeader`,
 * followed by a responsive grid of `Card` components. Each card displays an icon,
 * headline, and descriptive text for a feature. Spacing is applied between sections
 * to ensure consistent layout across screen sizes.
 * If `featuresOwners` is provided and marked with `forOwners`, an additional section
 * is rendered with its own headline and grid of feature cards targeted at owners.
 * This makes the component suitable for differentiating general features from
 * owner-specific ones.
 * The component is wrapped in MobX's `observer`, allowing it to reactively update
 * when observable data changes. A `testId` is also applied to the container for
 * end-to-end test targeting.
 *
 * @param props                The component props.
 * @param props.features       The list of general features, each containing an ID, headline, image, and text.
 * @param props.featuresOwners Optional owner-specific features section with headline and list of features.
 * @param props.hideBlock      An optional flag to hide the entire block when set to true. Defaults to `false`.
 * @param props.intro          Optional intro section configuration with overline, headline, and text.
 * @param props.testId         An optional identifier used for testing, applied as `data-cy`. Defaults to `'FeatureGrid'`.
 * @returns A JSX element rendering the feature grid with optional owner-specific features.
 *
 * @example
 * ```tsx
 * <FeatureGrid
 *   intro={{ headline: 'Our Features', overline: 'Discover', text: introText }}
 *   features={[
 *     { id: '1', headline: 'Fast Setup', image: setupIcon, text: setupText },
 *     { id: '2', headline: 'Secure', image: secureIcon, text: secureText }
 *   ]}
 *   featuresOwners={{
 *     forOwners: true,
 *     headline: 'Exclusive for Owners',
 *     features: [
 *       { id: '3', headline: 'Owner Dashboard', image: dashboardIcon, text: dashboardText }
 *     ]
 *   }}
 * />
 * ```
 */
const FeatureGrid = observer(({
    features,
    featuresOwners,
    hideBlock = false,
    id,
    intro,
    testId = 'FeatureGrid'
}: ComponentProps) => (hideBlock ? null : (
    <Container testId={testId}>
        <IntroHeader blockId={id} intro={intro} />
        <ViewportTrigger baseComponent={CardGrid}>
            {features?.map(feature => (
                <Card
                    key={feature.id}
                    headline={feature.headline}
                    iconImage={feature.image}
                    text={feature.text}
                />
            ))}
        </ViewportTrigger>
        {featuresOwners?.forOwners && (
            <>
                <Spacer y={{
                    xs: 18, md: 24
                }}
                />
                <ViewportTrigger baseComponent={HeadlineWrapper}>
                    <MotionH3 $align="center" variants={FadeInAnimation}>{featuresOwners.headline}</MotionH3>
                </ViewportTrigger>
                <Spacer y={{
                    xs: 18, md: 24
                }}
                />
                <ViewportTrigger baseComponent={CardGrid}>
                    {featuresOwners.features?.map(feature => (
                        <Card
                            key={feature.id}
                            headline={feature.headline}
                            iconImage={feature.image}
                            text={feature.text}
                        />
                    ))}
                </ViewportTrigger>
            </>
        )}
    </Container>
)));

FeatureGrid.displayName = 'FeatureGrid';

export {FeatureGrid};

const CardGrid = styled.div`
    display: grid;
    gap: ${spacing(8)};
    grid-template-columns: 1fr;

    ${media('sm')} {
        grid-template-columns: repeat(auto-fit, minmax(32rem, 1fr));
    }

    ${media('md')} {
        gap: ${spacing(6)};
    }
    ${media('lg')} {
        grid-template-columns: repeat(auto-fit, minmax(42rem, 1fr));
    }
`;

const HeadlineWrapper = styled.div`
    margin-inline: auto;
    max-width: 68.4rem;
    width: 100%;
`;

const MotionH3 = styled(motion.create(H3))``;